import {Check} from '@pinecast/common/icons';
import {SectionSubheaderH3} from '@pinecast/common/Text';
import type {DonationPage_podcast} from '@pinecast/gql/__generated_tj__/DonationPage';
import {FormattedMessage} from '@pinecast/i18n';
import {useCSS} from '@pinecast/styles';
import * as React from 'react';

/**
 * What a supporter gets whatever they give.
 *
 * These do not vary by amount, so repeating them inside each amount card would
 * be three identical lines three times over. Only the private episodes differ
 * between amounts, and those stay on the cards.
 *
 * Nor do they vary by frequency: a single month is a subscription that cancels
 * at the end of the period, so a listener who takes one month gets everything
 * a recurring supporter gets for as long as it runs.
 */
const TipJarIncludes = ({podcast}: {podcast: DonationPage_podcast}) => {
  const css = useCSS();
  const headingId = React.useId();

  const row = css({
    alignItems: 'center',
    display: 'flex',
    fontSize: '14px',
    gap: '9px',
    lineHeight: '20px',
  });

  return (
    <div
      className={css({
        alignItems: 'flex-start',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '20px',
        padding: '0 2px',
      })}
    >
      {/* Under the h2 of the amounts, which these go with. */}
      <SectionSubheaderH3
        id={headingId}
        style={{lineHeight: '20px', marginBottom: 0}}
      >
        <FormattedMessage
          description="Heading for the things a tip jar contribution includes no matter its size"
          defaultMessage="Every amount also includes"
        />
      </SectionSubheaderH3>
      {/* `list-style: none` takes the list away in WebKit, so the role puts
          it back. The check marks are hidden; the list says what they did. */}
      <ul
        aria-labelledby={headingId}
        className={css({
          display: 'flex',
          flexWrap: 'wrap',
          gap: '24px',
          listStyle: 'none',
          margin: 0,
          padding: 0,

          '@media (max-width: 600px)': {
            flexDirection: 'column',
            gap: '8px',
          },
        })}
        role="list"
      >
        <li className={row}>
          <Check size={16} />
          <FormattedMessage
            description="Item in the list of what a tip jar contribution includes"
            defaultMessage="Updates from the show"
          />
        </li>
        {podcast.feedbackActive && (
          <li className={row}>
            <Check size={16} />
            <FormattedMessage
              description="Item in the list of what a tip jar contribution includes"
              defaultMessage="Leave a comment for the show"
            />
          </li>
        )}
      </ul>
    </div>
  );
};

export default React.memo(TipJarIncludes);
